import { Snapshot, SnapshotType } from '@analyzer/client';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Chip,
  ChipProps,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSnapshotDestroyMutation, useSnapshotListQuery } from '../api/snapshot.js';
import { formatDateUTC, timeToNow } from '../components/utils.js';

function getSnapshotStatus(snapshot: Snapshot): JSX.Element {
  const hasError = !!snapshot.error;
  const label = hasError ? 'Error' : 'Success';
  const color: ChipProps['color'] = hasError ? 'error' : 'success';

  const chip = <Chip size="small" label={label} color={color} />;

  if (hasError && snapshot.error) {
    return (
      <Tooltip title={snapshot.error} arrow>
        {chip}
      </Tooltip>
    );
  }

  return chip;
}

type SnapshotListParams = {
  type: SnapshotType;
  projectId: string;
  resourceId: string;
};

export function SnapshotList({ type, projectId, resourceId }: SnapshotListParams) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: snapshots,
    isLoading,
    isError,
    error
  } = useSnapshotListQuery(type, projectId, resourceId, {
    limit: rowsPerPage,
    offset: page * rowsPerPage
  });
  const snapshotDestroy = useSnapshotDestroyMutation(type);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = (snapshotId: string) => {
    snapshotDestroy.mutate(
      { projectId, snapshotId },
      {
        onSuccess: () => enqueueSnackbar('Snapshot deleted', { variant: 'success' }),
        onError: (err) => enqueueSnackbar(`Failed to delete snapshot: ${err.message}`, { variant: 'error' })
      }
    );
  };

  const deletingSnapshotId = snapshotDestroy.variables?.snapshotId;

  if (isLoading) return <CircularProgress />;
  if (isError) return <Alert severity="error">{`Error: ${error.message}`}</Alert>;

  snapshots.sort((a, b) => b.updatedDate.getTime() - a.updatedDate.getTime());

  return (
    <TableContainer>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Analysis</TableCell>
            <TableCell>Date (UTC)</TableCell>
            <TableCell>Timestamp</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {snapshots.map((snapshot) => (
            <TableRow key={snapshot.id}>
              <TableCell>
                <Link to={`/projects/${projectId}/snapshots/${type}/${snapshot.id}`}>{snapshot.id}</Link>
              </TableCell>
              <TableCell>
                {snapshot.analyses[0] ? (
                  <Link to={`/projects/${projectId}/analyses/${type}/${snapshot.analyses[0]!.id}`}>
                    {snapshot.analyses[0]!.id}
                  </Link>
                ) : snapshot.analyzed ? (
                  'No Alerts'
                ) : (
                  'Processing'
                )}
              </TableCell>
              <TableCell>{formatDateUTC(snapshot.updatedDate)}</TableCell>
              <TableCell>{timeToNow(snapshot.updatedDate)}</TableCell>
              <TableCell>{getSnapshotStatus(snapshot)}</TableCell>
              <TableCell align="right">
                <IconButton
                  size="small"
                  aria-label="Delete snapshot"
                  onClick={(event) => {
                    event.preventDefault();
                    handleDelete(snapshot.id);
                  }}
                  disabled={snapshotDestroy.isLoading && deletingSnapshotId === snapshot.id}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              count={-1}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              showFirstButton={true}
              showLastButton={true}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
